import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import type { SavedSelection } from '../savedSelectionsStorage';

export type SaveSelectionResult = 'ok' | 'cap-exceeded' | 'storage-failed';

export interface SaveSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentIds: Set<string>;
  existingSelections: SavedSelection[];
  onSubmit: (
    args:
      | { kind: 'create'; name: string }
      | { kind: 'replace'; id: string; name: string },
  ) => SaveSelectionResult;
}

const SaveSelectionModal: React.FC<SaveSelectionModalProps> = ({
  isOpen,
  onClose,
  currentIds,
  existingSelections,
  onSubmit,
}) => {
  const [draft, setDraft] = useState('');
  const [storageError, setStorageError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraft('');
      setStorageError(false);
    }
  }, [isOpen]);

  const trimmed = draft.trim();

  const collisionEntry = useMemo<SavedSelection | undefined>(() => {
    if (!trimmed) return undefined;
    const key = trimmed.toLowerCase();
    return existingSelections.find(
      (s) => s.name.trim().toLowerCase() === key,
    );
  }, [existingSelections, trimmed]);

  const canSubmit = trimmed.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const result = collisionEntry
      ? onSubmit({ kind: 'replace', id: collisionEntry.id, name: trimmed })
      : onSubmit({ kind: 'create', name: trimmed });
    if (result === 'storage-failed') {
      setStorageError(true);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent mx="1rem">
        <ModalHeader>Save selection</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Input
              autoFocus
              value={draft}
              placeholder="Selection name"
              maxLength={60}
              onChange={(e) => {
                setDraft(e.target.value);
                if (storageError) setStorageError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            {collisionEntry && (
              <Text fontSize="sm" color="orange.600">
                Replace existing «{collisionEntry.name}»?
              </Text>
            )}
            {storageError && (
              <Text fontSize="sm" color="red.500">
                Couldn’t save. Storage unavailable.
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} w="100%">
            <Text fontSize="sm" color="gray.500">
              {currentIds.size.toLocaleString()} customers in selection
            </Text>
            <Spacer />
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!canSubmit}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SaveSelectionModal;
